import { useEffect, useRef, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import { useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { Box, Card, styled, TableRow, useTheme } from "@mui/material";

import { Block } from "./accountPieces";
import { AccountWrap, LiTableCell, shoppingTableHeadCells, shoppingTableSatuses } from "./components/pieces";
import { AccountHeader } from "./components/Typography";
import useMuiTable, { getComparator, stableSort } from "./components/useMuiTable";
import ShoppingTableActions from "./ShoppingComponents/ShoppingTableActions";
import LITableHead from "./components/LITableHead";
import ShoppingTableRow from "./ShoppingComponents/ShoppingTableRow";
import AppPagination from "./components/AppPagination";
import TableRowMobile from "./components/TableRowMobile";
import { Axios } from "../../common/utils";
import { baseApi } from "../../common/constants";
import Details from "./Details";
import type { Size, AppContext, Config, Purchase } from "../../types";

const TextLine = styled(Box)(() => ({
    textAlign: `center`,
    fontWeight: 700,
    fontSize: `18px`,
    padding: `50px 0 0`,
    color: ` #9e9e9e`,
}));

interface ShoppingProps {
    useMobileTables: boolean;
    editable: boolean;
    context: AppContext;
    setContext: (nv: Partial<Config>) => void;
}

const Shopping = ({ useMobileTables, editable, context, setContext }: ShoppingProps) => {
    const { search = ``, year = ``, status = `` } = useParams<{ search?: string; year?: string; status?: string }>();
    const [invoices, setInvoices] = useState<Purchase[]>([]);
    const [years, setYears] = useState<{ name: string; value: string }[] | undefined>();
    const [loading, setLoading] = useState(true);
    const [editingText, setEditingText] = useState(false);

    const navigate = useNavigate();
    const theme = useTheme();
    const [searchParams, setSearchParams] = useSearchParams();
    const ahRef = useRef<HTMLElement>(null);
    const sectionHeaders = context?.config.sectionHeaders || {};

    const showDetails = searchParams.get(`showDetails`);
    const mcb = searchParams.get(`mcb`);
    const closeDetails = () => setSearchParams({});
    const [size, setMissingTransaction] = useOutletContext<[Size, (open?: unknown) => void]>();

    useEffect(() => {
        Axios.get(
            `${baseApi}shoppingtrips?mode=year&includeyears=1${year ? `&modeselection=${year}` : ``}${
                status ? `&modestatus=${status}` : ``
            }`
        )
            .then(r => r.data)
            .then((d: { years: { name: string; value: string }[]; results: Purchase[] }) => {
                setYears(
                    d.years.length
                        ? d.years
                        : [{ name: new Date().getFullYear().toString(), value: new Date().getFullYear().toString() }]
                );
                setInvoices(d.results.map((x, i) => ({ i, ...x })) || []);
                setLoading(false);
                if (mcb) {
                    setMissingTransaction({});
                    setSearchParams({});
                }
            });
    }, []);

    const handleChangeFilter = (key: number, value: string) => {
        const path = window.location.pathname.split(`/`);
        path[2] = path[2] || years![0]?.value;
        path[3] = path[3] || shoppingTableSatuses[0]?.value;
        path[key] = value;
        navigate(encodeURI(path.join(`/`)));
    };
    const handleStatusFilter = (key: number, value: string) => {
        const path = window.location.pathname.split(`/`);
        path[2] = path[2] || years![0]?.value;
        path[3] = path[3] || shoppingTableSatuses[0]?.value;
        path[key] = value;
        navigate(encodeURI(path.join(`/`)));
    };
    const { page, order, setPage, orderBy, rowsPerPage, handleChangePage, handleRequestSort, handleChangeRowsPerPage } =
        useMuiTable({
            defaultOrderBy: `date`,
        });
    // rows are sorted by a dynamic orderBy key; bridge Purchase to the util's Record constraint through unknown[]
    const sortableInvoices: unknown[] = invoices;
    const sortedInvoices: unknown[] = stableSort(
        sortableInvoices as Record<string, string | number>[],
        getComparator(order, orderBy)
    );
    const filteredInvoices = (sortedInvoices as Purchase[]).filter(item =>
        item.store.toLowerCase().includes(search.toLowerCase())
    );

    useEffect(() => {
        if (!loading) {
            setInvoices([]);
            setLoading(true);
            Axios.get(`${baseApi}shoppingtrips?mode=year&modeselection=${year}&modestatus=${status}`)
                .then(r => r.data)
                .then(d => {
                    setInvoices(d.results || []);
                    setPage(0);
                    setLoading(false);
                });
        }
    }, [year, status]);

    return (
        <Block size={size}>
            <AccountWrap size={size}>
                {!!showDetails && <Details {...{ showDetails, closeDetails, size }} />}
                <AccountHeader
                    size={size}
                    ref={ahRef}
                    contentEditable={editingText}
                    suppressContentEditableWarning
                    dangerouslySetInnerHTML={{ __html: sectionHeaders.shopping || `Shopping History` }}
                    onBlur={v => {
                        setContext({ sectionHeaders: { ...sectionHeaders, shopping: v.target.innerHTML } });
                        setEditingText(false);
                    }}
                    {...(editable && !editingText
                        ? {
                              onClick: () => {
                                  setEditingText(true);
                                  setTimeout(() => {
                                      ahRef.current!.focus();
                                  }, 0);
                              },
                          }
                        : {})}
                    className={editingText ? `editingText` : editable ? `edit` : ``}
                />
                {!!filteredInvoices && !loading && (
                    <Box>
                        {size !== `small` && (
                            <Box
                                sx={{
                                    textAlign: `left`,
                                    fontSize: 20,
                                    color: `#000`,
                                    fontWeight: 600,
                                    verticalAlign: `top`,
                                    // size is narrowed to non-`small` here, but preserve the original conditional
                                    padding: (size as string) === `small` ? `0 24px` : ``,
                                }}
                                component="span"
                            >
                                Showing:&nbsp;&nbsp;
                            </Box>
                        )}
                        <ShoppingTableActions
                            popoverClick={() =>
                                context?.adminConfig?.mp_iframe_content!.use_our_missing_trans_flow
                                    ? setMissingTransaction({})
                                    : window.open(context?.adminConfig?.mp_iframe_content!.missing_aaa_dollars)
                            }
                            {...{ size, years, handleChangeFilter, shoppingTableSatuses, handleStatusFilter }}
                            filter={{ year, search, status }}
                            disabled={loading}
                        />
                        <Card
                            sx={{
                                borderRadius: `0 0 12px 12px`,
                                px: 3,
                                pb: 3,
                                background: theme.palette.account.background,
                            }}
                        >
                            <TableContainer
                                sx={{ borderRadius: `16px`, border: `1px solid #E1E1E1`, overflow: `hidden` }}
                            >
                                <Table
                                    sx={{
                                        border: `none`,
                                    }}
                                >
                                    <LITableHead
                                        rowCount={filteredInvoices.length}
                                        onRequestSort={handleRequestSort}
                                        headCells={shoppingTableHeadCells}
                                        {...{ useMobileTables, editable, setContext, order, orderBy, context }}
                                    />
                                    <TableBody sx={{ background: `#FFF` }}>
                                        {filteredInvoices
                                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                            .map(purchase =>
                                                useMobileTables ? (
                                                    <TableRowMobile
                                                        onClick={() =>
                                                            setSearchParams({
                                                                showDetails: encodeURIComponent(purchase.invoiceId),
                                                            })
                                                        }
                                                        key={purchase.i}
                                                        purchase={purchase}
                                                    />
                                                ) : (
                                                    <ShoppingTableRow
                                                        key={purchase.i}
                                                        {...{ purchase, setSearchParams }}
                                                    />
                                                )
                                            )}
                                        {filteredInvoices.length === 0 && !loading && (
                                            <TableRow>
                                                <LiTableCell colSpan={useMobileTables ? 1 : 6}>
                                                    <TextLine>No results found.</TextLine>
                                                </LiTableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Card>
                        <AppPagination
                            page={page}
                            component="div"
                            rowsPerPage={rowsPerPage}
                            count={filteredInvoices.length}
                            onPageChange={handleChangePage}
                            rowsPerPageOptions={[25, 50, 100]}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            slotProps={{ select: { MenuProps: { disableScrollLock: true } } }}
                        />
                    </Box>
                )}
            </AccountWrap>
        </Block>
    );
};

export default Shopping;
